import React, { useState, useEffect, useReducer } from "react";
import { lookupAPI, scheduleAPI } from "../services/api.ts";

interface ClassType {
  id: string | number;
  name: string;
}

interface PesticideOption {
  id: string | number;
  name: string;
}

interface ActiveIngredient {
  id: string | number;
  name: string;
}

interface ApplicationMethod {
  id: string | number;
  name: string;
}

interface PesticideEntry {
  id: number;
  recordId: number;
  pesticide: string;
  pesticideId: number;
  activeIngredients: string;
  ingredientId: number;
  class: string;
  classId: number;
  quantity: string;
  method: string;
  applicationId: number;
  remarks: string;
  isDirty: boolean;
}

interface TabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  tabs: { id: string; label: string }[];
}

// Custom mobile picker component
interface MobilePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (value: string, id: number) => void;
  options: {id: string | number; name: string}[];
  selectedValue: string;
  fieldId: number;
  fieldType: 'pesticide' | 'class' | 'activeIngredients' | 'method';
}

// Define the API request interface for data formatting
interface PesticidesRequest {
  Id: number;
  PesticideId: number;
  IngredientId: number;
  ClassId: number;
  QuantityOfFinal: string;
  ApplicationId: number;
  Remarks: string;
  IsDirty: boolean;
}

// Add props interface for PesticideInputForm
interface PesticideInputFormProps {
  scheduleId?: string | number;
  onSave?: (entries: PesticidesRequest[]) => void;
}

// Add type declaration for the counter used for debugging
interface WindowWithCounter extends Window {
  _pesticideApiCallCount?: number;
}

const MobilePicker: React.FC<MobilePickerProps> = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  options, 
  selectedValue,
  fieldId,
  fieldType
}) => {
  // Create a ref for the picker element
  const pickerRef = React.useRef<HTMLDivElement>(null);
  
  // Effect to handle click outside
  React.useEffect(() => {
    if (!isOpen) return;
    
    // Function to handle clicks outside the modal
    const handleClickOutside = (event: Event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    // Lock body scroll
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.body.style.overflow = originalStyle;
    };
  }, [isOpen, onClose]);
  
  // Early return if not open
  if (!isOpen) return null;
  
  const handleSelect = (value: string, id: number) => {
    console.log(`*** SELECTING: ${fieldType} = ${value} (ID: ${id}) ***`);
    
    // Call the parent's onSelect callback
    onSelect(value, id);
    
    // Close the picker immediately without delay
    onClose();
  };
  
  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-0 flex items-end justify-center transition-all duration-300 ease-out"
         style={{
           backgroundColor: 'rgba(0, 0, 0, 0.5)',
           animation: 'fadeIn 0.3s ease forwards'
         }}>
      <div 
        ref={pickerRef}
        className="w-full max-w-md bg-white rounded-t-lg shadow-lg overflow-hidden transform transition-transform duration-300 ease-out"
        style={{ 
          maxHeight: 'calc(80vh - 56px)',
          animation: 'slideUp 0.3s ease forwards'
        }}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white sticky top-0 z-10 shadow-sm">
          <h3 className="text-lg font-medium">Select Option</h3>
          <button 
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-500 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        {/* Options list */}
        <div className="overflow-auto p-2" style={{ maxHeight: 'calc(80vh - 120px)' }}>
          {options.map((option) => (
            <div
              key={option.id}
              className={`p-3 rounded-lg cursor-pointer mb-1 transition-colors duration-150 ${
                option.name === selectedValue 
                  ? 'bg-red-50 text-red-600 font-medium' 
                  : 'hover:bg-gray-50 active:bg-gray-100'
              }`}
              onClick={() => handleSelect(option.name, Number(option.id))}
            >
              {option.name}
              {option.name === selectedValue && (
                <span className="float-right">
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Tabs: React.FC<TabsProps> = ({ activeTab, setActiveTab, tabs }) => {
  return (
    <div className="border-b border-gray-200 mb-4">
      <div className="flex space-x-1 overflow-x-auto no-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`py-3 px-4 text-sm font-medium border-b-2 whitespace-nowrap ${
              activeTab === tab.id
                ? "border-red-600 text-red-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Add validation interface to track errors
interface ValidationErrors {
  [key: string]: {
    [key: string]: boolean;
  };
}

// Add a forceUpdate hook to ensure re-renders happen when needed
function useForceUpdate() {
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  return forceUpdate;
}

const PesticideInputForm: React.FC<PesticideInputFormProps> = ({ scheduleId, onSave }) => {
  // DEBUGGING: Log props immediately
  console.log("====== PesticideInputForm RENDERED ======");
  console.log("scheduleId:", scheduleId);
  console.log("scheduleId type:", typeof scheduleId);
  console.log("onSave provided:", !!onSave);
  console.log("=======================================");
  
  // Add the CSS animation keyframes to the document
  React.useEffect(() => {
    // Add keyframes for animations if they don't exist
    if (!document.getElementById('picker-animations')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'picker-animations';
      styleElement.innerHTML = `
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            background-color: rgba(0, 0, 0, 0);
          }
          to {
            background-color: rgba(0, 0, 0, 0.5);
          }
        }
      `;
      document.head.appendChild(styleElement);
    }
    
    // Clean up style element on unmount
    return () => {
      const styleElement = document.getElementById('picker-animations');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  const [activeTab, setActiveTab] = useState<string>("pesticides");
  const [entries, setEntries] = useState<PesticideEntry[]>([
    {
      id: 1,
      recordId: 0,
      pesticide: "",
      pesticideId: 0,
      activeIngredients: "",
      ingredientId: 0,
      class: "",
      classId: 0,
      quantity: "",
      method: "",
      applicationId: 0,
      remarks: "",
      isDirty: false
    }
  ]);
  const [originalPesticides, setOriginalPesticides] = useState<PesticideEntry[]>([]);
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [pesticideOptions, setPesticideOptions] = useState<PesticideOption[]>([]);
  const [activeIngredients, setActiveIngredients] = useState<ActiveIngredient[]>([]);
  const [applicationMethods, setApplicationMethods] = useState<ApplicationMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState<{id: number, field: 'pesticide' | 'class' | 'activeIngredients' | 'method'} | null>(null);
  
  // Add validation state
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Add state to track if data is loading
  const [dataLoading, setDataLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Replace lookupDataLoading with a simpler approach
  const [lookupDataInitialized, setLookupDataInitialized] = useState(false);

  // Add force update function 
  const forceUpdate = useForceUpdate();
  
  // Force UI update after picker selection
  const [refreshKey, setRefreshKey] = useState(0);

  // Add state for submission status
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const tabs = [
    { id: "pesticides", label: "Pesticide Records" },
    { id: "precautions", label: "Precautionary Statements" },
  ];

  // Fetch lookup data only once when component mounts
  useEffect(() => {
 
    
    // Only fetch lookup data if not already in cache
    const fetchLookupData = async () => {
      try {
        setLoading(true);
        console.log("Fetching lookup data (one-time)...");
        
        // Fetch all lookup data in parallel
        const [classData, pesticideData, ingredientsData, methodsData] = await Promise.all([
          lookupAPI.getClassTypes(),
          lookupAPI.getPesticideUsedSelection(),
          lookupAPI.getActiveIngredients(),
          lookupAPI.getApplicationMethods()
        ]);
        
        // Process results
        const classResults = classData.data || [];
        const pesticideResults = pesticideData.data || [];
        const ingredientResults = ingredientsData.data || [];
        const methodResults = methodsData.data || [];
        
        // Update state
        setClassTypes(classResults);
        setPesticideOptions(pesticideResults);
        setActiveIngredients(ingredientResults);
        setApplicationMethods(methodResults);
        
        // Cache the results in session storage
        const cacheData = {
          classTypes: classResults,
          pesticideOptions: pesticideResults,
          activeIngredients: ingredientResults,
          applicationMethods: methodResults,
          timestamp: new Date().getTime()
        };
        
      } catch (error) {
        console.error("Error fetching lookup data:", error);
      } finally {
        setLoading(false);
        setLookupDataInitialized(true);
      }
    };

    fetchLookupData();
  }, []); // Empty dependency array - only run once on mount

  // Fetch pesticide data separately when scheduleId changes
  useEffect(() => {
    // Only run if we have a scheduleId
    if (!scheduleId) {
      console.log("No scheduleId provided, skipping pesticide data fetch");
      return;
    }
    
    // Track if the component is still mounted
    let isMounted = true;
    
    // Helper function to process response data (avoids code duplication)
    const processResponseData = (pesticideData: any) => {
      let hasProcessedData = false;
      
      if (pesticideData && Array.isArray(pesticideData.data) && pesticideData.data.length > 0) {
        console.log(`Found ${pesticideData.data.length} pesticide records`);
        
        // Wait for lookup data to be available if not yet loaded
        if (lookupDataInitialized && 
            classTypes.length > 0 && 
            applicationMethods.length > 0) {
          
          const formattedEntries = pesticideData.data.map((item, index) => {
            // Find the matching application method name based on applicationId
            const applicationMethod = applicationMethods.find(
              method => Number(method.id) === item.applicationId
            );
            
            // Find the matching class name based on classId
            const classType = classTypes.find(
              classItem => Number(classItem.id) === item.classId
            );
            
            // Find matching pesticide and ingredient (if IDs are provided)
            const pesticide = item.pesticideId 
              ? pesticideOptions.find(p => Number(p.id) === item.pesticideId)
              : null;
            
            const ingredient = item.ingredientId
              ? activeIngredients.find(ing => Number(ing.id) === item.ingredientId)
              : null;
            
            console.log(`Processing pesticide record #${index + 1}:`, {
              id: item.id,
              applicationId: item.applicationId,
              applicationMethod: applicationMethod?.name,
              classId: item.classId,
              className: classType?.name,
              pesticideId: item.pesticideId,
              pesticide: pesticide?.name,
              ingredientId: item.ingredientId,
              ingredient: ingredient?.name,
              quantity: item.quantityOfFinal,
              remarks: item.remarks || ""
            });
            
            return {
              id: index + 1,
              recordId: item.id || 0,
              pesticide: pesticide?.name || "",
              pesticideId: item.pesticideId || 0,
              activeIngredients: ingredient?.name || "",
              ingredientId: item.ingredientId || 0,
              class: classType?.name || "",
              classId: item.classId || 0,
              quantity: item.quantityOfFinal || "",
              method: applicationMethod?.name || "",
              applicationId: item.applicationId || 0,
              remarks: item.remarks || "",
              isDirty: false
            };
          });
          
          if (formattedEntries.some(entry => entry.method || entry.applicationId)) {
            console.log("Found valid data in pesticide records, updating form");
            setEntries(formattedEntries);
            setOriginalPesticides([...formattedEntries]);
            hasProcessedData = true;
          }
        }
      }
      
      // If we didn't process any data or there was no relevant data in the response,
      // make sure we have at least one empty row
      if (!hasProcessedData) {
        console.log("No valid pesticide data found or lookup data not ready, using default empty row");
        setEntries([{ 
          id: 1, 
          recordId: 0, 
          pesticide: "", 
          pesticideId: 0, 
          activeIngredients: "", 
          ingredientId: 0, 
          class: "", 
          classId: 0, 
          quantity: "", 
          method: "", 
          applicationId: 0, 
          remarks: "",
          isDirty: false 
        }]);
        setOriginalPesticides([]);
      }
      
      setDataLoaded(true);
    };
    
    // Always fetch fresh data - no caching
    const fetchPesticideData = async () => {
      // Avoid setting state if component unmounted
      if (!isMounted) return;
      
      setDataLoading(true);
      console.log(`Fetching pesticide data for scheduleId: ${scheduleId}`);
      
      try {
        const pesticideData = await scheduleAPI.getPesticides(scheduleId);
        console.log("API Response:", pesticideData);
        
        // Avoid setting state if component unmounted
        if (!isMounted) return;
        
        processResponseData(pesticideData);
      } catch (error) {
        console.error("Error fetching pesticide data:", error);
        
        // Only set default row if component still mounted
        if (isMounted) {
          setEntries([{ 
            id: 1, 
            recordId: 0, 
            pesticide: "", 
            pesticideId: 0, 
            activeIngredients: "", 
            ingredientId: 0, 
            class: "", 
            classId: 0, 
            quantity: "", 
            method: "", 
            applicationId: 0, 
            remarks: "",
            isDirty: false 
          }]);
        }
      } finally {
        // Only update loading state if component still mounted
        if (isMounted) {
          setDataLoading(false);
        }
      }
    };

    fetchPesticideData();
    
    // Cleanup function to prevent memory leaks and setting state on unmounted component
    return () => {
      isMounted = false;
    };
  }, [scheduleId, lookupDataInitialized, classTypes, applicationMethods, pesticideOptions, activeIngredients]);

  // Process pesticide data when lookup data changes
  useEffect(() => {
    // Skip if we don't have both pesticide data and lookup data
    if (!originalPesticides.length || !lookupDataInitialized) {
      return;
    }
    
    console.log("Re-processing pesticide entries after lookup data update");
    
    // Re-map entries with updated lookup data
    const updatedEntries = originalPesticides.map((item) => {
      // Find the matching application method name based on applicationId
      const applicationMethod = applicationMethods.find(
        method => Number(method.id) === item.applicationId
      );
      
      // Find the matching class name based on classId
      const classType = classTypes.find(
        classItem => Number(classItem.id) === item.classId
      );
      
      // Find matching pesticide and ingredient (if IDs are provided)
      const pesticide = item.pesticideId 
        ? pesticideOptions.find(p => Number(p.id) === item.pesticideId)
        : null;
      
      const ingredient = item.ingredientId
        ? activeIngredients.find(ing => Number(ing.id) === item.ingredientId)
        : null;
      
      return {
        ...item,
        pesticide: pesticide?.name || item.pesticide,
        activeIngredients: ingredient?.name || item.activeIngredients,
        class: classType?.name || item.class,
        method: applicationMethod?.name || item.method
      };
    });
    
    setEntries(updatedEntries);
  }, [lookupDataInitialized, classTypes, applicationMethods, pesticideOptions, activeIngredients]);

  const addNewRow = () => {
    const newId = entries.length > 0 ? Math.max(...entries.map((e) => e.id)) + 1 : 1;
    setEntries([...entries, { 
      id: newId, 
      recordId: 0, 
      pesticide: "", 
      pesticideId: 0, 
      activeIngredients: "", 
      ingredientId: 0, 
      class: "", 
      classId: 0, 
      quantity: "", 
      method: "", 
      applicationId: 0, 
      remarks: "",
      isDirty: false 
    }]);
  };

  // Clear All function to reset the form
  const clearAll = () => {
    setEntries([{ 
      id: 1, 
      recordId: 0, 
      pesticide: "", 
      pesticideId: 0, 
      activeIngredients: "", 
      ingredientId: 0, 
      class: "", 
      classId: 0, 
      quantity: "", 
      method: "", 
      applicationId: 0, 
      remarks: "",
      isDirty: false 
    }]);
    // Force refresh the UI
    setRefreshKey(prev => prev + 1);
  };

  const removeRow = (id: number) => {
    if (entries.length > 1) {
      const entryToRemove = entries.find(entry => entry.id === id);
      
      // If this was an existing record (has a recordId)
      if (entryToRemove && entryToRemove.recordId > 0) {
        // Mark as dirty instead of removing
        setEntries(
          entries.map(entry => 
            entry.id === id 
              ? { ...entry, isDirty: true } 
              : entry
          )
        );
      } else {
        // For new records, just remove them
      setEntries(entries.filter((entry) => entry.id !== id));
      }
    }
  };

  const validateForm = (): boolean => {
    console.log("Validating form...");
    const newErrors: ValidationErrors = {};
    let isValid = true;

    // Check if we have any entries to save (non-dirty entries)
    const validEntries = entries.filter(entry => !entry.isDirty);
    if (validEntries.length === 0) {
      console.error("No valid entries to save");
      setSubmitError("No entries to save. Please add at least one pesticide record.");
      return false;
    }

    // Only validate entries that are not marked as dirty (not being deleted)
    validEntries.forEach(entry => {
      newErrors[entry.id] = {};
      
      // Check each required field
      if (!entry.pesticide) {
        newErrors[entry.id].pesticide = true;
        isValid = false;
        console.log(`Entry ${entry.id}: Missing pesticide`);
      }
      
      if (!entry.activeIngredients) {
        newErrors[entry.id].activeIngredients = true;
        isValid = false;
        console.log(`Entry ${entry.id}: Missing active ingredients`);
      }
      
      if (!entry.class) {
        newErrors[entry.id].class = true;
        isValid = false;
        console.log(`Entry ${entry.id}: Missing class`);
      }
      
      if (!entry.quantity) {
        newErrors[entry.id].quantity = true;
        isValid = false;
        console.log(`Entry ${entry.id}: Missing quantity`);
      }
      
      if (!entry.method) {
        newErrors[entry.id].method = true;
        isValid = false;
        console.log(`Entry ${entry.id}: Missing method`);
      }
    });

    setErrors(newErrors);
    console.log(`Form validation result: ${isValid ? 'VALID' : 'INVALID'}`);
    return isValid;
  };

  // Optimize submission logic to avoid unnecessary API calls
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted, preventing default");
    
    // Don't re-submit if already submitting
    if (submitting) {
      console.log("Submission already in progress, ignoring duplicate submit");
      return;
    }
    
    setFormSubmitted(true);
    console.log("Using scheduleId:", scheduleId);
    
    if (validateForm()) {
      // Form is valid, proceed with submission
      console.log("Validation passed, preparing API call");
      
      // Include all entries, including those marked as dirty (for deletion)
      // We only validate non-dirty entries
      const apiEntries = entries.map(entry => ({
        Id: entry.recordId,
        PesticideId: entry.pesticideId,
        IngredientId: entry.ingredientId,
        ClassId: entry.classId,
        QuantityOfFinal: entry.quantity,
        ApplicationId: entry.applicationId,
        Remarks: entry.remarks,
        PestControlScheduleId: typeof scheduleId === 'string' ? parseInt(scheduleId, 10) : scheduleId,
        IsDirty: entry.isDirty
      }));
      
      console.log("Formatted API entries:", apiEntries);
      
      try {
        setSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(false);
        
        const apiScheduleId = scheduleId || '';
        console.log(`Making API call with scheduleId: '${apiScheduleId}'`);
        
        // Use a debouncing approach to avoid duplicate calls
        const response = await scheduleAPI.updatePesticides(apiScheduleId, apiEntries);
        console.log("API response:", response);
        console.log("Pesticides updated successfully via API");
        setSubmitSuccess(true);
        
        // Call onSave callback if provided
        if (onSave) {
          console.log("Calling onSave callback function");
          onSave(apiEntries);
        }
      } catch (error) {
        console.error("Error updating pesticides:", error);
        setSubmitError("Failed to update pesticide data. Please try again.");
      } finally {
        setSubmitting(false);
      }
    } else {
      console.log("Form has validation errors");
      // Scroll to the first error
      const errorElement = document.querySelector('.error-field');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Create optimized versions of input handlers with debouncing
  const handleInputChange = (id: number, field: keyof PesticideEntry, value: string | number) => {
    console.log(`Updating ${field} for entry ${id} with value:`, value);
    
    // Create a new entries array with the updated entry
    const updatedEntries = entries.map((entry) => {
      if (entry.id !== id) return entry;
      
      // Create a copy of the entry
      const updatedEntry = { ...entry, [field]: value };
      
      // For dropdown fields, also update the ID fields
      if (field === 'pesticide') {
        const selectedPesticide = pesticideOptions.find(option => option.name === value);
        if (selectedPesticide) {
          updatedEntry.pesticideId = Number(selectedPesticide.id);
        }
      } else if (field === 'activeIngredients') {
        const selectedIngredient = activeIngredients.find(option => option.name === value);
        if (selectedIngredient) {
          updatedEntry.ingredientId = Number(selectedIngredient.id);
        }
      } else if (field === 'class') {
        const selectedClass = classTypes.find(option => option.name === value);
        if (selectedClass) {
          updatedEntry.classId = Number(selectedClass.id);
        }
      } else if (field === 'method') {
        const selectedMethod = applicationMethods.find(option => option.name === value);
        if (selectedMethod) {
          updatedEntry.applicationId = Number(selectedMethod.id);
        }
      } else if (field === 'quantity') {
        // Special handling for quantity field to ensure it doesn't disappear
        console.log(`Updating quantity for entry ${id} to:`, value);
        // Make sure we're preserving the value exactly as entered
        // If value is empty, keep it as empty string
        updatedEntry.quantity = value === '' ? '' : value.toString();
      }
      
      return updatedEntry;
    });
    
    // Update the state without forcing a refresh for quantity to prevent focus loss
    setEntries(updatedEntries);
    
    // Only update refresh key for non-quantity fields to prevent losing focus during typing
    if (field !== 'quantity') {
      setRefreshKey(prev => prev + 1);
    }
  };

  // Add back the picker selection handler
  const handlePickerSelect = (id: number, field: 'pesticide' | 'class' | 'activeIngredients' | 'method', value: string, optionId: number) => {
    console.log(`Selected ${field}: ${value} (ID: ${optionId})`);
    
    // Directly update the form state with the selected value
    handleInputChange(id, field, value);
    
    // Close the picker
    setPickerOpen(null);
  };

  // Helper function to check if a field has an error
  const hasError = (entryId: number, field: keyof PesticideEntry): boolean => {
    return formSubmitted && !!errors[entryId]?.[field];
  };

  // Function to render required field marker
  const requiredFieldMark = () => (
    <span className="text-red-600 ml-1">*</span>
  );

  return (
    <div className="w-full" key={refreshKey}>
      {dataLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <span className="ml-2 text-gray-600">Loading pesticide data...</span>
        </div>
      ) : (
        <>
      <div className="mb-4">
        <h2 className="text-lg font-medium text-gray-900 mb-1">Pesticide Application Records</h2>
            <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Record all pesticide applications with their active ingredients, class, quantity, and method.
        </p>
            </div>
      </div>

      <div>
        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs} />

        <form onSubmit={handleSubmit}>
          {activeTab === "pesticides" && (
            <>
              {/* Table View (Desktop) */}
              <div className="hidden md:block mb-6">
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-red-600 text-white">
                            <th className="border border-red-700 p-2 text-sm">Pesticide(s) Used {requiredFieldMark()}</th>
                            <th className="border border-red-700 p-2 text-sm">Active Ingredients (A.I) {requiredFieldMark()}</th>
                            <th className="border border-red-700 p-2 text-sm">Class {requiredFieldMark()}</th>
                            <th className="border border-red-700 p-2 text-sm">Quantity (Lit/Kg) {requiredFieldMark()}</th>
                            <th className="border border-red-700 p-2 text-sm">Method Of Application {requiredFieldMark()}</th>
                            <th className="border border-red-700 p-2 text-sm">Remarks</th>
                        <th className="border border-red-700 p-2 text-sm w-16">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                          {entries.filter(entry => !entry.isDirty).map((entry) => (
                        <tr key={entry.id} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="border border-gray-300 p-2">
                                <select
                              value={entry.pesticide}
                              onChange={(e) => handleInputChange(entry.id, "pesticide", e.target.value)}
                                  className={`w-full p-2 border rounded text-sm appearance-none bg-white relative focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    hasError(entry.id, "pesticide") ? "border-red-500 bg-red-50" : "border-gray-300"
                                  }`}
                                  disabled={loading}
                                  style={{
                                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23666666'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E\")",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "right 0.5rem center",
                                    backgroundSize: "1.5em 1.5em",
                                    paddingRight: "2.5rem"
                                  }}
                                >
                                  <option value="">Select pesticide</option>
                                  {pesticideOptions.map((option) => (
                                    <option 
                                      key={option.id} 
                                      value={option.name}
                                      className="py-3" 
                                      style={{padding: "10px", lineHeight: "1.5"}}
                                    >
                                      {option.name}
                                    </option>
                                  ))}
                                </select>
                                {hasError(entry.id, "pesticide") && (
                                  <p className="text-red-500 text-xs mt-1">This field is required</p>
                                )}
                          </td>
                          <td className="border border-gray-300 p-2">
                                <select
                              value={entry.activeIngredients}
                              onChange={(e) => handleInputChange(entry.id, "activeIngredients", e.target.value)}
                                  className={`w-full p-2 border rounded text-sm appearance-none bg-white relative focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    hasError(entry.id, "activeIngredients") ? "border-red-500 bg-red-50" : "border-gray-300"
                                  }`}
                                  disabled={loading}
                                  style={{
                                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23666666'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E\")",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "right 0.5rem center",
                                    backgroundSize: "1.5em 1.5em",
                                    paddingRight: "2.5rem"
                                  }}
                                >
                                  <option value="">Select active ingredient</option>
                                  {activeIngredients.map((ingredient) => (
                                    <option 
                                      key={ingredient.id} 
                                      value={ingredient.name}
                                      className="py-3" 
                                      style={{padding: "10px", lineHeight: "1.5"}}
                                    >
                                      {ingredient.name}
                                    </option>
                                  ))}
                                </select>
                                {hasError(entry.id, "activeIngredients") && (
                                  <p className="text-red-500 text-xs mt-1">This field is required</p>
                                )}
                          </td>
                          <td className="border border-gray-300 p-2">
                                <select
                              value={entry.class}
                              onChange={(e) => handleInputChange(entry.id, "class", e.target.value)}
                                  className={`w-full p-2 border rounded text-sm appearance-none bg-white relative focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    hasError(entry.id, "class") ? "border-red-500 bg-red-50" : "border-gray-300"
                                  }`}
                                  disabled={loading}
                                  style={{
                                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23666666'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E\")",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "right 0.5rem center",
                                    backgroundSize: "1.5em 1.5em",
                                    paddingRight: "2.5rem"
                                  }}
                                >
                                  <option value="">Select class</option>
                                  {classTypes.map((classType) => (
                                    <option 
                                      key={classType.id} 
                                      value={classType.name}
                                      className="py-3" 
                                      style={{padding: "10px", lineHeight: "1.5"}}
                                    >
                                      {classType.name}
                                    </option>
                                  ))}
                                </select>
                                {hasError(entry.id, "class") && (
                                  <p className="text-red-500 text-xs mt-1">This field is required</p>
                                )}
                          </td>
                          <td className="border border-gray-300 p-2">
                                <div className={hasError(entry.id, "quantity") ? "error-field" : ""}>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Quantity of Final Conc. (Lit/Kg) {requiredFieldMark()}
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.1"
                                    value={entry.quantity || ""}
                                    onChange={(e) => handleInputChange(entry.id, "quantity", e.target.value)}
                                    className={`w-full p-2 border rounded text-sm ${
                                      hasError(entry.id, "quantity") ? "border-red-500 bg-red-50" : "border-gray-300"
                                    }`}
                                    placeholder="Enter quantity"
                                  />
                                  {hasError(entry.id, "quantity") && (
                                    <p className="text-red-500 text-xs mt-1">This field is required</p>
                                  )}
                                </div>
                          </td>
                          <td className="border border-gray-300 p-2">
                                <select
                              value={entry.method}
                              onChange={(e) => handleInputChange(entry.id, "method", e.target.value)}
                                  className={`w-full p-2 border rounded text-sm appearance-none bg-white relative focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    hasError(entry.id, "method") ? "border-red-500 bg-red-50" : "border-gray-300"
                                  }`}
                                  disabled={loading}
                                  style={{
                                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23666666'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E\")",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "right 0.5rem center",
                                    backgroundSize: "1.5em 1.5em",
                                    paddingRight: "2.5rem"
                                  }}
                                >
                                  <option value="">Select method</option>
                                  {applicationMethods.map((method) => (
                                    <option 
                                      key={method.id} 
                                      value={method.name}
                                      className="py-3" 
                                      style={{padding: "10px", lineHeight: "1.5"}}
                                    >
                                      {method.name}
                                    </option>
                                  ))}
                                </select>
                                {hasError(entry.id, "method") && (
                                  <p className="text-red-500 text-xs mt-1">This field is required</p>
                                )}
                          </td>
                          <td className="border border-gray-300 p-2">
                                <textarea
                                  value={entry.remarks || ""}
                                  onChange={(e) => handleInputChange(entry.id, "remarks", e.target.value)}
                                  className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Enter remarks (optional)"
                                  rows={2}
                                />
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            <button
                              type="button"
                              onClick={() => removeRow(entry.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                              aria-label="Remove row"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Card View (Mobile) */}
              <div className="md:hidden space-y-6 mb-6">
                {entries.filter(entry => !entry.isDirty).map((entry) => (
                  <div key={entry.id} className="bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                      <div className="font-medium">Record #{entry.id}</div>
                      <button
                        type="button"
                        onClick={() => removeRow(entry.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        disabled={loading}
                        aria-label="Remove record"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"></path>
                        </svg>
                      </button>
                    </div>
                    <div className="p-4 space-y-4">
                      <div className={hasError(entry.id, "pesticide") ? "error-field" : ""}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pesticide(s) Used {requiredFieldMark()}
                        </label>
                        <div 
                          className={`w-full p-2 border rounded text-sm bg-white relative ${
                            hasError(entry.id, "pesticide") ? "border-red-500 bg-red-50" : 
                            entry.pesticide ? "border-green-500 bg-green-50" : "border-gray-300"
                          }`}
                          onClick={() => setPickerOpen({id: entry.id, field: 'pesticide'})}
                        >
                          <div className="flex justify-between items-center">
                            <span className={entry.pesticide ? "text-gray-900 font-medium" : "text-gray-400"}>
                              {entry.pesticide || "Select pesticide"}
                            </span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                          {entry.pesticide && (
                            <div className="absolute right-8 top-1/2 -translate-y-1/2">
                              <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        {hasError(entry.id, "pesticide") && (
                          <p className="text-red-500 text-xs mt-1">This field is required</p>
                        )}
                        <MobilePicker 
                          isOpen={pickerOpen?.id === entry.id && pickerOpen?.field === 'pesticide'}
                          onClose={() => setPickerOpen(null)}
                          onSelect={(value, id) => handlePickerSelect(entry.id, 'pesticide', value, id)}
                          options={pesticideOptions}
                          selectedValue={entry.pesticide}
                          fieldId={entry.id}
                          fieldType="pesticide"
                        />
                      </div>
                      
                      <div className={hasError(entry.id, "activeIngredients") ? "error-field" : ""}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Active Ingredients (A.I) {requiredFieldMark()}
                        </label>
                        <div 
                          className={`w-full p-2 border rounded text-sm bg-white relative ${
                            hasError(entry.id, "activeIngredients") ? "border-red-500 bg-red-50" : 
                            entry.activeIngredients ? "border-green-500 bg-green-50" : "border-gray-300"
                          }`}
                          onClick={() => setPickerOpen({id: entry.id, field: 'activeIngredients'})}
                        >
                          <div className="flex justify-between items-center">
                            <span className={entry.activeIngredients ? "text-gray-900 font-medium" : "text-gray-400"}>
                              {entry.activeIngredients || "Select active ingredient"}
                            </span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                          {entry.activeIngredients && (
                            <div className="absolute right-8 top-1/2 -translate-y-1/2">
                              <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        {hasError(entry.id, "activeIngredients") && (
                          <p className="text-red-500 text-xs mt-1">This field is required</p>
                        )}
                        <MobilePicker 
                          isOpen={pickerOpen?.id === entry.id && pickerOpen?.field === 'activeIngredients'}
                          onClose={() => setPickerOpen(null)}
                          onSelect={(value, id) => handlePickerSelect(entry.id, 'activeIngredients', value, id)}
                          options={activeIngredients}
                          selectedValue={entry.activeIngredients}
                          fieldId={entry.id}
                          fieldType="activeIngredients"
                        />
                      </div>
                      
                      <div className={hasError(entry.id, "class") ? "error-field" : ""}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Class {requiredFieldMark()}
                        </label>
                        <div 
                          className={`w-full p-2 border rounded text-sm bg-white relative ${
                            hasError(entry.id, "class") ? "border-red-500 bg-red-50" : 
                            entry.class ? "border-green-500 bg-green-50" : "border-gray-300"
                          }`}
                          onClick={() => setPickerOpen({id: entry.id, field: 'class'})}
                        >
                          <div className="flex justify-between items-center">
                            <span className={entry.class ? "text-gray-900 font-medium" : "text-gray-400"}>
                              {entry.class || "Select class"}
                            </span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                          {entry.class && (
                            <div className="absolute right-8 top-1/2 -translate-y-1/2">
                              <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        {hasError(entry.id, "class") && (
                          <p className="text-red-500 text-xs mt-1">This field is required</p>
                        )}
                        <MobilePicker 
                          isOpen={pickerOpen?.id === entry.id && pickerOpen?.field === 'class'}
                          onClose={() => setPickerOpen(null)}
                          onSelect={(value, id) => handlePickerSelect(entry.id, 'class', value, id)}
                          options={classTypes}
                          selectedValue={entry.class}
                          fieldId={entry.id}
                          fieldType="class"
                        />
                      </div>
                      
                      <div className={hasError(entry.id, "quantity") ? "error-field" : ""}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Quantity of Final Conc. (Lit/Kg) {requiredFieldMark()}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={entry.quantity || ""}
                          onChange={(e) => handleInputChange(entry.id, "quantity", e.target.value)}
                          className={`w-full p-2 border rounded text-sm ${
                            hasError(entry.id, "quantity") ? "border-red-500 bg-red-50" : 
                            entry.quantity ? "border-green-500 bg-green-50" : "border-gray-300"
                          }`}
                          placeholder="Enter quantity"
                        />
                        {hasError(entry.id, "quantity") && (
                          <p className="text-red-500 text-xs mt-1">This field is required</p>
                        )}
                      </div>
                      
                      <div className={hasError(entry.id, "method") ? "error-field" : ""}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Method Of Application {requiredFieldMark()}
                        </label>
                        <div 
                          className={`w-full p-2 border rounded text-sm bg-white relative ${
                            hasError(entry.id, "method") ? "border-red-500 bg-red-50" : 
                            entry.method ? "border-green-500 bg-green-50" : "border-gray-300"
                          }`}
                          onClick={() => setPickerOpen({id: entry.id, field: 'method'})}
                        >
                          <div className="flex justify-between items-center">
                            <span className={entry.method ? "text-gray-900 font-medium" : "text-gray-400"}>
                              {entry.method || "Select method"}
                            </span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                          {entry.method && (
                            <div className="absolute right-8 top-1/2 -translate-y-1/2">
                              <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        {hasError(entry.id, "method") && (
                          <p className="text-red-500 text-xs mt-1">This field is required</p>
                        )}
                        <MobilePicker 
                          isOpen={pickerOpen?.id === entry.id && pickerOpen?.field === 'method'}
                          onClose={() => setPickerOpen(null)}
                          onSelect={(value, id) => handlePickerSelect(entry.id, 'method', value, id)}
                          options={applicationMethods}
                          selectedValue={entry.method}
                          fieldId={entry.id}
                          fieldType="method"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Remarks
                        </label>
                        <textarea
                          value={entry.remarks || ""}
                          onChange={(e) => handleInputChange(entry.id, "remarks", e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter remarks (optional)"
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

                  <div className="mt-4 mb-6 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={addNewRow}
                  className="flex items-center justify-center bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors w-full md:w-auto"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0h-3v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add New Pesticide Record
                </button>
                    
                    <button
                      type="button"
                      onClick={clearAll}
                      className="flex items-center justify-center bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors w-full md:w-auto"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010-2H4zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                      </svg>
                      Clear All
                </button>
              </div>
            </>
          )}

          {activeTab === "precautions" && (
            <div className="bg-white rounded-lg border border-gray-200 mb-6">
              <h3 className="font-medium text-gray-800 mb-3 p-4 border-b">Precautionary Statement Guide</h3>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-4">
                  Please refer to the following precautionary statements based on pesticide classification:
                </p>
                
                <table className="w-full border-collapse mb-4">
                  <thead>
                    <tr className="bg-red-600 text-white">
                      <th className="border border-red-700 p-2 text-sm text-left">Precautionary Statement:</th>
                      <th className="border border-red-700 p-2 text-sm text-left">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="border border-gray-300 p-2 text-sm font-medium">Class II</td>
                      <td className="border border-gray-300 p-2 text-sm">
                        <span className="text-red-600 font-medium">Poisonous.</span> Keep away from foodstuff and children
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="border border-gray-300 p-2 text-sm font-medium">Class III</td>
                      <td className="border border-gray-300 p-2 text-sm">
                        <span className="text-amber-600 font-medium">Harmful.</span> Keep away from foodstuff and children
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="border border-gray-300 p-2 text-sm font-medium">Class IV</td>
                      <td className="border border-gray-300 p-2 text-sm">
                        Keep away from foodstuff and children.
                      </td>
                    </tr>
                  </tbody>
                </table>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 text-sm">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-yellow-700">
                        <strong>Note:</strong> Always follow the safety instructions on the product label. Use appropriate personal protective equipment (PPE) when handling pesticides.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t">
                {/* Success Message */}
                {submitSuccess && (
                  <div className="flex items-center text-green-600 mr-4">
                    <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Record saved successfully!</span>
                  </div>
                )}
                
                {/* Error Message */}
                {submitError && (
                  <div className="flex items-center text-red-600 mr-4">
                    <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>{submitError}</span>
                  </div>
                )}
                
            <button
              type="submit"
                  disabled={submitting}
                  className={`${
                    submitting ? "bg-green-400" : "bg-green-600 hover:bg-green-700"
                  } text-white px-4 py-2 rounded transition-colors flex items-center justify-center min-w-[120px]`}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    "Save Record"
                  )}
            </button>
          </div>
        </form>
      </div>
        </>
      )}
    </div>
  );
};

export default PesticideInputForm; 